'use client';

import React, { useRef } from 'react';
import Link from 'next/link';
import StackedHero from '@/components/our-work/StackedHero';

/* ── Fashion Brand SEO Illustration ── */
function FashionSEOIllustration() {
    const points = [
        [0, 88], [8, 84], [16, 80], [24, 75], [32, 70], [40, 62],
        [50, 55], [60, 46], [70, 38], [80, 30], [90, 20], [100, 10],
    ];
    const toSvg = (pts: number[][], w: number, h: number) =>
        pts.map(([x, y]) => `${(x / 100) * w},${(y / 100) * h}`).join(' ');
    const W = 820, H = 320;
    const lineStr = toSvg(points, W, H);
    const areaStr = `${(0 / 100) * W},${H} ` + lineStr + ` ${W},${H}`;

    return (
        <svg viewBox={`0 0 ${W} ${H}`} fill="none" xmlns="http://www.w3.org/2000/svg"
            style={{ width: '100%', height: '100%', position: 'absolute', inset: 0 }}>
            {/* Grid lines */}
            {[0.25, 0.5, 0.75].map((y, i) => (
                <line key={i} x1="0" y1={H * y} x2={W} y2={H * y}
                    stroke="rgba(255,255,255,0.05)" strokeWidth="1" strokeDasharray="6 6" />
            ))}
            {[0.2, 0.4, 0.6, 0.8].map((x, i) => (
                <line key={i} x1={W * x} y1="0" x2={W * x} y2={H}
                    stroke="rgba(255,255,255,0.04)" strokeWidth="1" strokeDasharray="6 6" />
            ))}

            {/* Area fill */}
            <defs>
                <linearGradient id="fashionAreaGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ceff1a" stopOpacity="0.18" />
                    <stop offset="100%" stopColor="#ceff1a" stopOpacity="0" />
                </linearGradient>
                <filter id="glowLine">
                    <feGaussianBlur stdDeviation="3" result="blur" />
                    <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                </filter>
            </defs>
            <polygon points={areaStr} fill="url(#fashionAreaGrad)" />

            {/* Line */}
            <polyline points={lineStr} stroke="#ceff1a" strokeWidth="2.5"
                strokeLinecap="round" strokeLinejoin="round" filter="url(#glowLine)" />

            {/* Data points */}
            {[[0, 88], [40, 62], [70, 38], [100, 10]].map(([x, y], i) => (
                <g key={i}>
                    <circle cx={(x / 100) * W} cy={(y / 100) * H} r="5"
                        fill="#0f0f0f" stroke="#ceff1a" strokeWidth="2" />
                </g>
            ))}

            {/* Final point callout */}
            <g>
                <circle cx={W} cy={H * 0.10} r="7" fill="#ceff1a" />
                <rect x={W - 110} y={H * 0.10 - 28} width="96" height="24" rx="6"
                    fill="rgba(206,255,26,0.12)" stroke="rgba(206,255,26,0.3)" strokeWidth="1" />
                <text x={W - 62} y={H * 0.10 - 11} textAnchor="middle"
                    fill="#ceff1a" fontSize="11" fontFamily="sans-serif" fontWeight="700"
                    letterSpacing="1">7,750+ clicks</text>
            </g>

            {/* Start point callout */}
            <g>
                <circle cx={0} cy={H * 0.88} r="7" fill="rgba(255,255,255,0.2)" stroke="rgba(255,255,255,0.4)" strokeWidth="2" />
                <rect x={10} y={H * 0.88 - 14} width="72" height="22" rx="6"
                    fill="rgba(255,255,255,0.07)" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
                <text x={46} y={H * 0.88 + 3} textAnchor="middle"
                    fill="rgba(255,255,255,0.4)" fontSize="10" fontFamily="sans-serif" fontWeight="600"
                    letterSpacing="0.5">671 clicks</text>
            </g>

            {/* Month labels */}
            {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'].map((m, i) => (
                <text key={m} x={(i / 5) * W + (i === 5 ? -10 : i === 0 ? 10 : 0)}
                    y={H - 8} textAnchor="middle"
                    fill="rgba(255,255,255,0.2)" fontSize="10" fontFamily="sans-serif" letterSpacing="1">{m}</text>
            ))}
        </svg>
    );
}

/* ── Healthcare SEO Illustration ── */
function HealthcareSEOIllustration() {
    const W = 820, H = 320;

    const bars = [
        { label: 'Organic\nSessions', before: 22897, after: 80082, max: 80082 },
        { label: 'Events', before: 165518, after: 456880, max: 456880 },
        { label: 'Revenue\n(₹)', before: 133665, after: 192937, max: 192937 },
    ];

    const barW = 72, groupGap = 120, startX = 110;

    return (
        <svg viewBox={`0 0 ${W} ${H}`} fill="none" xmlns="http://www.w3.org/2000/svg"
            style={{ width: '100%', height: '100%', position: 'absolute', inset: 0 }}>
            <defs>
                <linearGradient id="afterBarGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ceff1a" stopOpacity="1" />
                    <stop offset="100%" stopColor="#ceff1a" stopOpacity="0.55" />
                </linearGradient>
                <linearGradient id="beforeBarGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="rgba(255,255,255,0.18)" stopOpacity="1" />
                    <stop offset="100%" stopColor="rgba(255,255,255,0.05)" stopOpacity="1" />
                </linearGradient>
                <filter id="barGlow">
                    <feGaussianBlur stdDeviation="4" result="blur" />
                    <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                </filter>
            </defs>

            {/* Grid lines */}
            {[0.25, 0.5, 0.75].map((y, i) => (
                <line key={i} x1="40" y1={H * y} x2={W - 20} y2={H * y}
                    stroke="rgba(255,255,255,0.05)" strokeWidth="1" strokeDasharray="5 5" />
            ))}

            {/* Baseline */}
            <line x1="40" y1={H - 48} x2={W - 20} y2={H - 48}
                stroke="rgba(255,255,255,0.1)" strokeWidth="1" />

            {bars.map((b, i) => {
                const cx = startX + i * (barW * 2 + groupGap);
                const maxH = H - 80;
                const beforeH = Math.max(8, (b.before / b.max) * maxH);
                const afterH = maxH;
                const baseline = H - 48;

                const pct = Math.round(((b.after - b.before) / b.before) * 100);

                return (
                    <g key={b.label}>
                        {/* Before bar */}
                        <rect x={cx - barW - 4} y={baseline - beforeH} width={barW} height={beforeH}
                            rx="6" fill="url(#beforeBarGrad)" />

                        {/* After bar */}
                        <rect x={cx + 4} y={baseline - afterH} width={barW} height={afterH}
                            rx="6" fill="url(#afterBarGrad)" filter="url(#barGlow)" />

                        {/* % badge on after bar */}
                        <rect x={cx + 4 + barW / 2 - 28} y={baseline - afterH - 32} width={56} height={22} rx={11}
                            fill="rgba(206,255,26,0.15)" stroke="rgba(206,255,26,0.35)" strokeWidth={1} />
                        <text x={cx + 4 + barW / 2} y={baseline - afterH - 16} textAnchor="middle"
                            fill="#ceff1a" fontSize="11" fontFamily="sans-serif" fontWeight="800">+{pct}%</text>

                        {/* Label */}
                        {b.label.split('\n').map((line, li) => (
                            <text key={li} x={cx + 4 + barW / 2 - barW / 2 - 4 + barW / 2}
                                y={baseline + 16 + li * 13} textAnchor="middle"
                                fill="rgba(255,255,255,0.35)" fontSize="10"
                                fontFamily="sans-serif" letterSpacing="0.5">{line}</text>
                        ))}
                    </g>
                );
            })}

            {/* Legend */}
            <g>
                <rect x={W - 200} y={16} width={12} height={12} rx={3}
                    fill="rgba(255,255,255,0.18)" />
                <text x={W - 183} y={26} fill="rgba(255,255,255,0.3)" fontSize="10"
                    fontFamily="sans-serif">Before</text>
                <rect x={W - 120} y={16} width={12} height={12} rx={3} fill="#ceff1a" />
                <text x={W - 103} y={26} fill="rgba(255,255,255,0.5)" fontSize="10"
                    fontFamily="sans-serif">After</text>
            </g>
        </svg>
    );
}

const caseStudies = [
    {
        name: 'Fashion Brand SEO',
        tag: 'SEO · Organic Growth',
        stat: '1,000%',
        statLabel: 'Organic Traffic Growth',
        industry: 'Fashion & Apparel',
        href: '/our-work/case-studies/fashion-brand-seo',
        accent: '#ceff1a',
        bg: '#f5efe8',
        image: '/images/our-work/fashion-brand-seo-cover.webp',
        Illustration: FashionSEOIllustration,
    },
    {
        name: 'Healthcare E-commerce SEO',
        tag: 'SEO · E-commerce · Revenue Growth',
        stat: '249%',
        statLabel: 'Organic Sessions Growth',
        industry: 'Healthcare & Wellness',
        href: '/our-work/case-studies/healthcare-ecommerce-seo',
        accent: '#ceff1a',
        bg: '#0a0f0a',
        image: '/images/our-work/healthcare-seo-cover.webp',
        Illustration: HealthcareSEOIllustration,
    },
];

export default function CaseStudyPage() {
    return (
        <main style={{ background: '#0f0f0f', minHeight: '100vh', overflow: 'clip' }}>

            <StackedHero
                title={<>Real Results.<br />Real Growth.</>}
                description="In-depth breakdowns of how we drive measurable growth for our clients — from strategy to execution to results."
            >

                {/* ── Case Study Cards ── */}
                <section style={{
                    padding: '80px 95px 120px',
                    margin: '0 auto',
                    display: 'flex',
                    width: '100%',
                    maxWidth: 1920,
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 64,
                    boxSizing: 'border-box',
                }}>
                    {caseStudies.map((cs) => (
                        <CaseStudyCard key={cs.name} cs={cs} />
                    ))}
                </section>
            </StackedHero>
        </main>
    );
}

function CaseStudyCard({ cs }: { cs: typeof caseStudies[0] }) {
    const cardRef = useRef<HTMLDivElement>(null);
    const { Illustration } = cs;
    const hasImage = 'image' in cs && cs.image;

    return (
        <div ref={cardRef} style={{ display: 'flex', flexDirection: 'column', gap: 20, width: '100%' }}>
            <Link href={cs.href} style={{ display: 'block', textDecoration: 'none' }}>
                <div
                    style={{
                        width: '100%',
                        height: 'clamp(300px, 42vw, 680px)',
                        borderRadius: 40,
                        overflow: 'hidden',
                        background: cs.bg,
                        position: 'relative',
                        cursor: 'pointer',
                        transition: 'transform 0.4s ease, box-shadow 0.4s ease',
                    }}
                    onMouseEnter={e => {
                        (e.currentTarget as HTMLDivElement).style.transform = 'scale(1.015)';
                        (e.currentTarget as HTMLDivElement).style.boxShadow = '0 24px 64px rgba(0,0,0,0.22)';
                    }}
                    onMouseLeave={e => {
                        (e.currentTarget as HTMLDivElement).style.transform = 'scale(1)';
                        (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
                    }}
                >
                    {hasImage ? (
                        /* ── Photo card ── */
                        <>
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img
                                src={(cs as typeof cs & { image: string }).image}
                                alt={cs.name}
                                style={{
                                    position: 'absolute', inset: 0,
                                    width: '100%', height: '100%',
                                    objectFit: 'cover', objectPosition: 'center',
                                    zIndex: 0,
                                    transition: 'transform 0.6s ease',
                                }}
                            />
                            {/* Dark overlay so text stays readable */}
                            <div style={{
                                position: 'absolute', inset: 0, zIndex: 1,
                                background: 'linear-gradient(135deg, rgba(8,4,0,0.55) 0%, rgba(8,4,0,0.25) 60%, rgba(8,4,0,0.5) 100%)',
                            }} />
                        </>
                    ) : (
                        /* ── SVG illustration card ── */
                        <>
                            {/* Subtle grid overlay */}
                            <div style={{
                                position: 'absolute', inset: 0, opacity: 0.035, zIndex: 0,
                                backgroundImage: 'linear-gradient(rgba(206,255,26,1) 1px, transparent 1px), linear-gradient(90deg, rgba(206,255,26,1) 1px, transparent 1px)',
                                backgroundSize: '60px 60px',
                            }} />
                            {/* SVG Illustration */}
                            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '68%', zIndex: 1 }}>
                                <Illustration />
                            </div>
                            {/* Fade overlay */}
                            <div style={{
                                position: 'absolute', inset: 0, zIndex: 2,
                                background: `linear-gradient(180deg, ${cs.bg} 0%, ${cs.bg} 28%, transparent 60%)`,
                                pointerEvents: 'none',
                            }} />
                        </>
                    )}

                    {/* Industry tag — only show on SVG cards */}
                    {!hasImage && (
                        <div style={{
                            position: 'absolute', top: 32, left: 40, zIndex: 4,
                            background: 'rgba(206,255,26,0.15)',
                            border: '1px solid rgba(206,255,26,0.4)',
                            borderRadius: 50,
                            padding: '8px 20px',
                            fontFamily: 'var(--font-manrope)',
                            fontWeight: 700,
                            fontSize: 12,
                            color: '#ceff1a',
                            letterSpacing: '0.12em',
                            textTransform: 'uppercase',
                        }}>
                            {cs.industry}
                        </div>
                    )}

                    {/* Stat + label — only show on SVG cards */}
                    {!hasImage && (
                        <div style={{
                            position: 'absolute', top: '18%', left: 0, right: 0,
                            textAlign: 'center', zIndex: 4,
                        }}>
                            <div style={{
                                fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                                fontWeight: 700,
                                fontSize: 'clamp(52px, 8vw, 120px)',
                                color: cs.accent,
                                lineHeight: 1,
                                letterSpacing: '-0.02em',
                            }}>
                                {cs.stat}
                            </div>
                            <div style={{
                                fontFamily: 'var(--font-manrope)',
                                fontWeight: 500,
                                fontSize: 'clamp(12px, 1.4vw, 18px)',
                                color: 'rgba(251,253,253,0.5)',
                                letterSpacing: '0.12em',
                                textTransform: 'uppercase',
                                marginTop: 8,
                            }}>
                                {cs.statLabel}
                            </div>
                        </div>
                    )}

                    {/* Arrow CTA */}
                    <div style={{
                        position: 'absolute', bottom: 32, right: 40, zIndex: 4,
                        width: 52, height: 52,
                        borderRadius: '50%',
                        background: '#ceff1a',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M4 10h12M10 4l6 6-6 6" stroke="#080400" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                </div>
            </Link>

            {/* Card footer */}
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px 20px', paddingLeft: 8 }}>
                <Link href={cs.href} style={{ textDecoration: 'none' }}>
                    <span style={{
                        fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                        fontWeight: 400,
                        fontSize: 'clamp(18px, 2vw, 28px)',
                        color: '#080400',
                        letterSpacing: '0.02em',
                    }}>
                        {cs.name}
                    </span>
                </Link>
                <svg width="44" height="44" viewBox="0 0 44 44" fill="none" style={{ flexShrink: 0 }}>
                    <line x1="38" y1="6" x2="6" y2="38" stroke="#080400" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <span style={{
                    fontFamily: 'var(--font-manrope)',
                    fontWeight: 400,
                    fontSize: 'clamp(14px, 1.5vw, 20px)',
                    color: '#444',
                    letterSpacing: '0.02em',
                }}>
                    {cs.tag}
                </span>
            </div>
        </div>
    );
}
